import { useState, useEffect } from "react";
import { useQuery, useMutation, useAction } from "convex/react";
import { api } from "convex/_generated/api";
import {
  Calendar, Plus, Check, X, Clock, EyeOff, Pencil, ChevronDown, ChevronUp,
} from "lucide-react";
import { StudioLayout } from "../../components/layout/StudioLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { DanceStyleSelect } from "../../components/forms/DanceStyleSelect";
import { TimeSelect, followMeridiem } from "../../components/forms/TimeSelect";
import { endsAfterMidnight, LATE_NIGHT_TAG } from "../../lib/timeOfDay";
import { LessonSection } from "../../components/forms/LessonSection";
import type { LessonValue } from "../../components/forms/LessonSection";
import { EventEditModal } from "../../components/forms/EventEditModal";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { cn } from "../../lib/utils";
import type { Doc, Id } from "convex/_generated/dataModel";
import { formatTime } from "@/lib/dateUtils";

const STATUS_BADGE = {
  pending: { label: "Pending", className: "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20" },
  approved: { label: "Approved", className: "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20" },
  hidden: { label: "Hidden", className: "text-muted-foreground bg-secondary" },
  archived: { label: "Archived", className: "text-muted-foreground bg-secondary" },
};

function formatDate(iso: string) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

interface NewEventFormProps {
  onClose: () => void;
  ownedVenueId?: Id<"venues">;
  ownedVenueName?: string;
}

function NewEventForm({ onClose, ownedVenueId, ownedVenueName }: NewEventFormProps) {
  const submitEvent = useAction(api.events.submitEventWithGeocode);
  const { user } = useCurrentUser();
  const venues = useQuery(api.venues.getVenuesByOwner);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [venueId, setVenueId] = useState<Id<"venues"> | null>(null);
  const [venueName, setVenueName] = useState("");
  const [venueAddress, setVenueAddress] = useState("");

  // Populate venue once the query resolves — useState initial values are only
  // read on mount, at which point useQuery hasn't loaded yet.
  useEffect(() => {
    if (!venues || venueId !== null) return; // already set, or still loading
    const preferred = ownedVenueId
      ? venues.find((v) => v._id === ownedVenueId)
      : undefined;
    const target = preferred ?? venues[0];
    if (target) {
      setVenueId(target._id);
      setVenueName(ownedVenueName ?? target.name);
      setVenueAddress(target.address ?? "");
    }
  }, [venues]);
  const [lesson, setLesson] = useState<LessonValue | null>(null);
  const [danceStyles, setDanceStyles] = useState<string[]>([]);
  const [mainStyles, setMainStyles] = useState<string[]>([]);
  const [eventUrl, setEventUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!title.trim()) { setError("Title is required."); return; }
    if (!date) { setError("Date is required."); return; }
    if (!startTime) { setError("Start time is required."); return; }
    if (!venueName.trim()) { setError("Venue is required."); return; }
    if (danceStyles.length === 0) { setError("Select at least one dance style."); return; }

    setSaving(true);
    setError(null);
    try {
      const selectedVenue = venues?.find((v) => v._id === venueId);
      await submitEvent({
        title: title.trim(),
        description: description.trim() || undefined,
        date,
        startTime,
        endTime: endTime || undefined,
        lessonStartTime: lesson?.startTime || undefined,
        lessonEndTime: lesson?.endTime || undefined,
        venueName: venueName.trim(),
        venueAddress: venueAddress || undefined,
        venueId: venueId ?? undefined,
        venueLatitude: selectedVenue?.latitude,
        venueLongitude: selectedVenue?.longitude,
        danceStyles,
        mainStyles,
        // This form has no tag picker, but an end time past midnight is the
        // one tag the times themselves already answer.
        tags: endsAfterMidnight(startTime, endTime) ? [LATE_NIGHT_TAG] : undefined,
        eventUrl: eventUrl.trim() || undefined,
        submittedBy: user?.email ?? "",
      });
      onClose();
    } catch (e: any) {
      setError(e.message ?? "Failed to create event.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border p-5 mb-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground">New Event</h3>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{error}</p>
      )}

      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">Title</label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Event title" />
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">Description</label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="Optional details"
        />
      </div>

      {/* Date takes the whole first row; an hour, a minute and AM/PM need
              more width than a third of one. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-foreground mb-1.5">Date</label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Start Time</label>
          <TimeSelect value={startTime} onChange={setStartTime} />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">End Time</label>
          <TimeSelect
            value={endTime}
            onChange={setEndTime}
            placeholder="Optional"
            defaultMeridiem={followMeridiem(startTime)}
          />
        </div>
      </div>

      <LessonSection
        eventStartTime={startTime}
        value={lesson}
        onChange={setLesson}
      />

      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">Venue</label>
        {venues === undefined ? (
          <div className="h-9 bg-secondary/30 rounded-md animate-pulse" />
        ) : venues.length === 0 ? (
          <p className="text-sm text-muted-foreground">No venues registered yet. Add a venue in the Overview tab first.</p>
        ) : venues.length === 1 ? (
          <div className="flex items-center gap-2 bg-secondary/40 rounded-md px-3 py-2 text-sm">
            <span className="font-medium text-foreground">{venues[0]!.name}</span>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground truncate">{venues[0]!.address}</span>
          </div>
        ) : (
          <select
            value={venueId ?? ""}
            onChange={(e) => {
              const v = venues.find((v) => v._id === e.target.value);
              if (v) { setVenueId(v._id); setVenueName(v.name); setVenueAddress(v.address); }
            }}
            className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm text-foreground"
          >
            {venues.map((v) => (
              <option key={v._id} value={v._id}>{v.name}</option>
            ))}
          </select>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">Event URL</label>
        <Input value={eventUrl} onChange={(e) => setEventUrl(e.target.value)} placeholder="https://" type="url" />
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Dance Styles</label>
        <DanceStyleSelect
          value={danceStyles}
          onChange={setDanceStyles}
          mainStyles={mainStyles}
          onMainStylesChange={setMainStyles}
        />
      </div>

      <div className="flex gap-2 pt-1">
        <Button onClick={handleSubmit} disabled={saving}>
          {saving ? "Creating..." : "Create Event"}
        </Button>
        <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
      </div>
    </div>
  );
}

export function StudioEventsPage() {
  const { user, isLoading } = useCurrentUser();
  const events = useQuery(api.events.getAllEventsAtOwnedVenues);
  const approveEvent = useMutation(api.events.approveEventAsVenueOwner);
  const rejectEvent = useMutation(api.events.rejectEventAsVenueOwner);

  const [showNewForm, setShowNewForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Doc<"events"> | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  if (isLoading) {
    return (
      <StudioLayout>
        <div className="py-12 text-center text-muted-foreground">Loading...</div>
      </StudioLayout>
    );
  }

  if (!user || user.accountType !== "studio") {
    return (
      <StudioLayout>
        <div className="py-12 text-center text-muted-foreground">This page is only available to studio accounts.</div>
      </StudioLayout>
    );
  }

  const pendingEvents = events?.filter((e) => e.status === "pending") ?? [];
  const activeEvents = events?.filter((e) => e.status === "approved") ?? [];
  const inactiveEvents = events?.filter((e) => e.status === "hidden" || e.status === "archived") ?? [];

  return (
    <StudioLayout>
      {/* ── Pending Approvals ── */}
      {pendingEvents.length > 0 && (
        <section className="mb-8">
          <h2 className="font-semibold text-lg text-foreground mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500" />
            Pending Submissions
            <span className="text-xs font-bold bg-amber-500 text-white px-2 py-0.5 rounded-full">
              {pendingEvents.length}
            </span>
          </h2>
          <div className="space-y-3">
            {pendingEvents.map((event) => (
              <div key={event._id} className="bg-card rounded-xl border border-amber-200 dark:border-amber-800 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-foreground">{event.title}</h3>
                    <p className="text-sm text-muted-foreground">{formatDate(event.date)} · {formatTime(event.startTime)}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Submitted by {event.submittedBy}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-400 dark:border-emerald-800 dark:hover:bg-emerald-900/20"
                      onClick={() => approveEvent({ eventId: event._id })}
                    >
                      <Check className="w-3.5 h-3.5" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-destructive border-destructive/30 hover:bg-destructive/10"
                      onClick={() => rejectEvent({ eventId: event._id })}
                    >
                      <X className="w-3.5 h-3.5" />
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Create Event ── */}
      <div className="mb-6">
        {!showNewForm && (
          <Button onClick={() => setShowNewForm(true)}>
            <Plus className="w-4 h-4" />
            New Event
          </Button>
        )}
        {showNewForm && (
          <NewEventForm onClose={() => setShowNewForm(false)} />
        )}
      </div>

      {/* ── Active Events ── */}
      <section className="mb-6">
        <h2 className="font-semibold text-lg text-foreground mb-3 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Events at My Venues
        </h2>

        {events === undefined ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <div key={i} className="h-16 bg-secondary/30 rounded-lg animate-pulse" />)}
          </div>
        ) : activeEvents.length === 0 && pendingEvents.length === 0 ? (
          <div className="text-center py-10 bg-card rounded-xl border border-border/50">
            <Calendar className="w-10 h-10 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-muted-foreground mb-3">No events yet.</p>
            <Button size="sm" onClick={() => setShowNewForm(true)}>
              <Plus className="w-4 h-4" />
              Create Your First Event
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {activeEvents.map((event) => {
              const badge = STATUS_BADGE[event.status];
              return (
                <div
                  key={event._id}
                  className="flex items-center justify-between bg-card rounded-xl border border-border/50 px-4 py-3 gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-foreground text-sm">{event.title}</span>
                      <span className={cn("text-xs px-2 py-0.5 rounded-full", badge.className)}>
                        {badge.label}
                      </span>
                      {event.patternId && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">Recurring</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {formatDate(event.date)} · {event.venueName}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="flex-shrink-0"
                    onClick={() => setEditingEvent(event)}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline ml-1">Edit</span>
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Hidden / Archived ── */}
      {inactiveEvents.length > 0 && (
        <section>
          <button
            onClick={() => setShowInactive(!showInactive)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3"
          >
            {showInactive ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            <EyeOff className="w-4 h-4" />
            Hidden & Archived ({inactiveEvents.length})
          </button>
          {showInactive && (
            <div className="space-y-2">
              {inactiveEvents.map((event) => {
                const badge = STATUS_BADGE[event.status];
                return (
                  <div
                    key={event._id}
                    className="flex items-center justify-between bg-card rounded-xl border border-border/50 px-4 py-3 gap-3 opacity-60"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-foreground text-sm">{event.title}</span>
                        <span className={cn("text-xs px-2 py-0.5 rounded-full", badge.className)}>
                          {badge.label}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {formatDate(event.date)} · {event.venueName}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="flex-shrink-0"
                      onClick={() => setEditingEvent(event)}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline ml-1">Edit</span>
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {editingEvent && (
        <EventEditModal
          event={editingEvent}
          onClose={() => setEditingEvent(null)}
          onSaved={() => setEditingEvent(null)}
        />
      )}
    </StudioLayout>
  );
}
